import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { specials } from "@/mocks/specials";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

const HOT_KEYWORDS = ["星际", "悬疑", "高分科幻", "治愈", "反转", "动画"];

const genreName = (key: string) =>
  categories.find((c) => c.key === key)?.name ?? key;

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [visible, setVisible] = useState(8);
  const { isFavorite, toggleFavorite } = useFavorites();

  useSeo({
    title: query
      ? `“${query}” 搜索结果 - 欧美大片影视网`
      : "搜索电影 - 欧美大片影视网",
    description: query
      ? `欧美大片影视网关于“${query}”的搜索结果，包含相关电影与影评文章。`
      : "在欧美大片影视网搜索欧美电影、影评关键词，快速找到你想看的内容。",
    keywords: "电影搜索,欧美电影,影评搜索,电影推荐",
  });

  const q = query.trim().toLowerCase();

  const matchedMovies = useMemo(() => {
    if (!q) return [];
    return movies.filter((m) => {
      const genreText = m.genres.map(genreName).join(" ");
      const haystack = [
        m.title,
        m.titleEn,
        m.synopsis,
        m.reviewExcerpt,
        m.director,
        m.cast.join(" "),
        m.tags.join(" "),
        genreText,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [q]);

  const matchedArticles = useMemo(() => {
    if (!q) return [];
    const list: { id: string; title: string; excerpt: string; movieId: string; specialTitle: string }[] = [];
    specials.forEach((sp) => {
      sp.articles.forEach((a) => {
        const haystack = `${a.title} ${a.excerpt}`.toLowerCase();
        if (haystack.includes(q)) {
          list.push({
            id: a.id,
            title: a.title,
            excerpt: a.excerpt,
            movieId: a.movieId,
            specialTitle: sp.title,
          });
        }
      });
    });
    return list;
  }, [q]);

  const total = matchedMovies.length + matchedArticles.length;
  const shownMovies = matchedMovies.slice(0, visible);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const next = keyword.trim();
    setVisible(8);
    setParams(next ? { q: next } : {});
  };

  const runKeyword = (word: string) => {
    setKeyword(word);
    setVisible(8);
    setParams({ q: word });
  };

  return (
    <SiteLayout>
      <section className="w-full bg-primary-950 text-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl mb-2">
            搜索电影与影评
          </h1>
          <p className="text-sm text-background-300/90 mb-6">
            输入电影名称、类型或影评关键词，快速找到你想看的内容。
          </p>
          <form
            onSubmit={handleSubmit}
            className="flex items-center gap-2 bg-background-50 rounded-full pl-5 pr-1.5 py-1.5 max-w-2xl"
          >
            <i className="ri-search-line text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="例如：星际、悬疑、回春…"
              className="flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none py-1"
            />
            <button
              type="submit"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
        {query ? (
          <div className="mb-6">
            <h2 className="text-sm md:text-base text-foreground-600">
              关键词
              <strong className="text-primary-600 mx-1.5">“{query}”</strong>
              共找到
              <strong className="text-primary-600 mx-1.5">{total}</strong>
              条结果（电影 {matchedMovies.length} 部，影评 {matchedArticles.length} 篇）
            </h2>
          </div>
        ) : (
          <div className="mb-6">
            <h2 className="text-sm md:text-base text-foreground-600">
              输入关键词开始搜索，或试试下面的热门搜索：
            </h2>
            <div className="flex flex-wrap gap-2 mt-3">
              {HOT_KEYWORDS.map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => runKeyword(word)}
                  className="px-4 py-2 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-fire-line mr-1 text-accent-600" />
                  {word}
                </button>
              ))}
            </div>
          </div>
        )}

        {query && total === 0 && (
          <div className="rounded-lg bg-background-100 border border-background-200 p-10 md:p-16 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
              <i className="ri-search-eye-line text-3xl" />
            </div>
            <h3 className="font-heading font-bold text-lg text-foreground-800 mb-2">
              没有找到与“{query}”相关的内容
            </h3>
            <p className="text-sm text-foreground-500 mb-6">
              换个关键词试试，或从下面的热门搜索开始探索。
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {HOT_KEYWORDS.map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => runKeyword(word)}
                  className="px-4 py-2 rounded-full bg-background-50 border border-background-300 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        )}

        {matchedMovies.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-film-line text-primary-600" />
              <h2 className="font-heading font-bold text-lg text-foreground-900">
                相关电影
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {shownMovies.map((movie) => (
                <MovieCard
                  key={movie.id}
                  movie={movie}
                  showExcerpt
                  isFavorite={isFavorite(movie.id)}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
            {visible < matchedMovies.length && (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + 8)}
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-md border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  加载更多电影 <i className="ri-arrow-down-line" />
                </button>
              </div>
            )}
          </div>
        )}

        {matchedArticles.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-article-line text-primary-600" />
              <h2 className="font-heading font-bold text-lg text-foreground-900">
                相关影评文章
              </h2>
            </div>
            <div className="space-y-3">
              {matchedArticles.map((a) => (
                <Link
                  key={a.id}
                  to={`/detail/${a.movieId}`}
                  className="group block rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors p-4 md:p-5 cursor-pointer"
                >
                  <span className="inline-block px-2 py-0.5 rounded bg-accent-100 text-accent-900 text-[11px] font-medium mb-2">
                    {a.specialTitle}
                  </span>
                  <h3 className="font-heading font-bold text-base text-foreground-900 group-hover:text-primary-600 transition-colors mb-1.5">
                    {a.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                    {a.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}